import { motion } from "framer-motion";
import { Megaphone, Cpu, Bot, PenTool, Globe, TrendingUp } from "lucide-react";

const PILLARS = [
    { label: "Marketing", icon: Megaphone },
    { label: "Technology", icon: Cpu },
    { label: "Automation", icon: Bot },
    { label: "Content", icon: PenTool },
    { label: "Websites", icon: Globe },
    { label: "Performance Ads", icon: TrendingUp },
];

const STATS = [
    { v: "8", l: "services, one system" },
    { v: "360°", l: "of the journey covered" },
    { v: "24/7", l: "the queue never sleeps" },
];

export const Positioning = () => (
    <section id="why" data-testid="positioning-section" className="py-24 lg:py-32">
        <div className="mx-auto grid max-w-6xl items-center gap-16 px-5 lg:grid-cols-2 lg:px-8">
            <motion.div
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
                <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-inkblue">Why 24queue</p>
                <h2 className="mt-4 font-display text-3xl font-bold leading-[1.12] tracking-tight text-ink sm:text-4xl lg:text-5xl">
                    We don't just run ads. <span className="highlighter px-1">We build systems</span> that bring businesses <span className="text-inkblue">customers.</span>
                </h2>
                <p className="mt-4 rotate-[-1.5deg] font-hand text-2xl text-inkblue">
                    not six disconnected vendors — one connected growth engine
                </p>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-ink/65">
                    Marketing, technology, automation, content, websites and performance advertising — fused into a single
                    system that moves people from stranger, to lead, to appointment, to repeat customer.
                </p>
                <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {PILLARS.map((p, i) => (
                        <motion.div
                            key={p.label}
                            data-testid={`pillar-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="flex items-center gap-2.5 border-2 border-ink/15 bg-[#FFFDF6] px-3 py-2.5 transition-colors duration-300 hover:border-inkblue"
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.06 }}
                        >
                            <p.icon size={16} className="shrink-0 text-inkblue" />
                            <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-ink/80">{p.label}</span>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.div
                className="relative"
                initial={{ opacity: 0, y: 36, rotate: 4 }}
                whileInView={{ opacity: 1, y: 0, rotate: 2 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
                <div className="relative border-2 border-ink bg-white p-3 pb-12 shadow-[10px_10px_0_#14171C]">
                    <div className="absolute -top-3 left-1/4 h-6 w-24 rotate-[-5deg] bg-hl/80" />
                    <div className="absolute -top-3 right-1/4 h-6 w-24 rotate-[4deg] bg-hl/80" />
                    <img
                        src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHx0ZWNoJTIwc3RhcnR1cCUyMG1vZGVybiUyMG9mZmljZSUyMHdvcmtzcGFjZXxlbnwwfHx8fDE3OTAyODA2NzZ8MA&ixlib=rb-4.1.0&q=85"
                        alt="The 24queue growth team at work"
                        data-testid="positioning-image"
                        className="h-64 w-full object-cover grayscale-[30%] sm:h-80"
                        loading="lazy"
                    />
                    <p className="absolute bottom-3 left-0 w-full text-center font-hand text-2xl text-inkblue">
                        where the queue gets built
                    </p>
                </div>
                <div className="mt-10 grid grid-cols-3 gap-4">
                    {STATS.map((s) => (
                        <div key={s.l} data-testid={`stat-${s.l.split(",")[0].replace(/\s+/g, "-")}`} className="border-l-2 border-inkblue pl-3">
                            <div className="font-mono text-2xl font-bold text-inkblue sm:text-3xl">{s.v}</div>
                            <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-ink/50">{s.l}</div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </div>
    </section>
);
